import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { ColorOptionDto2, ColorStepDto, SelectMode } from '@/api-sdk';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';
import { isScrolledIntoView, scrollStepIntoView } from '@/utils';

import '../../form/fg-toggle';
import '../fg-configurator-step';
import './color-version/fg-color-options-list';

@customElement('fg-configurator-step-color')
@localized()
export class FgConfiguratorStepColor extends LitElement {
  @property({ type: String })
  stepTitle = '';

  @property({ type: Array })
  colorOptions?: ColorOptionDto2[];

  @property({ type: String })
  selectMode: SelectMode = SelectMode.SINGLE;

  @property({ type: String })
  selectModeSwitchQueryString: ColorStepDto['selectModeSwitchQueryString'] = '';

  @property({ type: String })
  validationMessage?: string | null = null;

  @property({ type: Boolean })
  showValidationMessage = false;

  @property({ type: Boolean })
  scrollToStep = false;

  @state()
  highlightMultiColorToggle = false;

  @state()
  switchColorCount = 0;

  renderSelectTemplate() {
    return html`
      <div
        slot="step-header"
        class="toggle-container ${classMap({
          'highlight-toggle-container': this.highlightMultiColorToggle,
        })}"
      >
        <fg-toggle
          @toggle-event=${() => this.switchSelectMode()}
          .showLabel=${true}
          .checked=${this.selectMode === SelectMode.MULTI}
          .label=${t('actions.selectMultipleColors') || ''}
        ></fg-toggle>
      </div>
      ${this.selectMode === SelectMode.SINGLE
        ? this.renderOptionsList()
        : html`
            <div class="options-wrapper">
              <p>
                ${t('content.configurator.chooseColorStep.sameColorNotice')}
              </p>
              ${this.renderOptionsList()}
            </div>
          `}
    `;
  }

  highlightColorToggle(event: CustomEvent) {
    if (!Object.values(event.detail).every(Boolean)) return;
    this.switchColorCount++;
    const highlightDurationInMs = 3000;
    if (this.selectMode === SelectMode.SINGLE && this.switchColorCount === 2) {
      this.highlightMultiColorToggle = true;
      setTimeout(() => {
        this.highlightMultiColorToggle = false;
      }, highlightDurationInMs);
    }
  }

  renderOptionsList() {
    return html`
      <fg-color-options-list
        .stepTitle=${this.stepTitle}
        .options=${this.colorOptions}
        .isMultiSelectMode=${this.selectMode === SelectMode.MULTI}
        @color-selected=${this.highlightColorToggle}
      ></fg-color-options-list>
    `;
  }

  render() {
    return html`
      <fg-configurator-step
        .stepTitle=${t('headers.chooseProduct')}
        .validationMessage=${this.validationMessage}
        .showValidationMessage=${this.showValidationMessage}
      >
        ${this.selectModeSwitchQueryString !== null
          ? this.renderSelectTemplate()
          : this.renderOptionsList()}
      </fg-configurator-step>
    `;
  }

  switchSelectMode() {
    this.switchColorCount = 0;
    if (!this.selectModeSwitchQueryString) {
      return;
    }
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString: this.selectModeSwitchQueryString,
          },
        },
      }),
    );
  }

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (_changedProperties.has('scrollToStep') && this.scrollToStep) {
      scrollStepIntoView(this);
      isScrolledIntoView(this);
    }
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .options-wrapper {
        margin-top: 10px;
      }

      .toggle-container.highlight-toggle-container {
        border: 1px solid var(--fg-success, var(--fg-fallback-success));
        background-color: var(
          --fg-success--light,
          var(--fg-fallback-success--light)
        );
      }

      .toggle-container {
        display: inline-flex;
        padding: 14px 16px;
        border-radius: 5px;
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border: 1px solid var(--fg-gray--100, var(--fg-fallback-gray--100));
        align-items: center;
        transition:
          background-color 0.3s linear,
          border 0.3s linear;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .options-wrapper {
          padding: 15px 10px;
          margin: -1px -10px;
        }

        .select-mode-tab-button {
          padding: 7px 10px;
          margin: 0 -10px;
        }
      }
    `,
  ];
}
